/** Inline controls on a Support board card so nothing needs opening: priority, category, assignee, title edit, contact details. */
import React, { useState } from 'react';
import { Check, Mail, Pencil, Phone, Plus, X } from 'lucide-react';

const selectCls = 'h-6 text-[10px] font-semibold rounded border border-slate-200 bg-white text-slate-700 px-1 max-w-full';

export function CardToolbar({ ticket: t, teammates, priorities, categories, onPatch }) {
  return (
    <div className="flex flex-wrap gap-1 mt-1.5" onClick={(e) => e.stopPropagation()} data-testid={`support-toolbar-${t.id}`}>
      <select value={t.priority || 'standard'} onChange={(e) => onPatch(t, { priority: e.target.value })} className={selectCls} aria-label="Priority" data-testid={`support-priority-${t.id}`}>
        {priorities.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
      </select>
      <select value={t.category || ''} onChange={(e) => onPatch(t, { category: e.target.value })} className={selectCls} aria-label="Category" data-testid={`support-category-${t.id}`}>
        <option value="">No category</option>
        {categories.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
      </select>
      <select value={t.assigned_to || ''} onChange={(e) => onPatch(t, { assigned_to: e.target.value })} className={`${selectCls} ${t.assigned_to ? 'bg-slate-900 text-white border-slate-900' : ''}`} aria-label="Assign to" data-testid={`support-assign-${t.id}`}>
        <option value="">Unassigned</option>
        {teammates.map((m) => <option key={m.user_id} value={m.user_id}>{m.name}</option>)}
      </select>
    </div>
  );
}

export function EditableTitle({ ticket: t, onPatch }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(t.title || '');
  if (!editing) {
    return (
      <div className="group/title flex items-center gap-1 min-w-0">
        <div className="font-semibold text-sm text-gray-900 truncate" title={t.title || 'Support'} data-testid={`support-title-${t.id}`}>{t.title || 'Support'}</div>
        <button type="button" onClick={(e) => { e.stopPropagation(); setVal(t.title || ''); setEditing(true); }} className="opacity-0 group-hover/title:opacity-100 focus:opacity-100 text-slate-400 hover:text-slate-700 p-0.5" aria-label="Edit title" data-testid={`support-title-edit-${t.id}`}><Pencil className="w-3 h-3" /></button>
      </div>
    );
  }
  const save = async () => { if (val.trim() && val.trim() !== t.title) await onPatch(t, { title: val.trim() }); setEditing(false); };
  return (
    <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
      <input autoFocus value={val} onChange={(e) => setVal(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setEditing(false); }} maxLength={120} className="flex-1 min-w-0 h-7 text-sm font-semibold border border-slate-300 rounded px-1.5" data-testid={`support-title-input-${t.id}`} />
      <button type="button" onClick={save} className="p-1 rounded bg-slate-900 text-white" aria-label="Save title" data-testid={`support-title-save-${t.id}`}><Check className="w-3 h-3" /></button>
      <button type="button" onClick={() => setEditing(false)} className="p-1 rounded border text-slate-500" aria-label="Cancel"><X className="w-3 h-3" /></button>
    </div>
  );
}

function ContactField({ ticket: t, field, icon: Icon, href, placeholder, type, onPatch }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState('');
  const current = t[field];
  if (editing) {
    const save = async () => { if (val.trim()) await onPatch(t, { [field]: val.trim() }); setEditing(false); };
    return (
      <span className="inline-flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
        <input autoFocus type={type} value={val} onChange={(e) => setVal(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setEditing(false); }} placeholder={placeholder} className="h-6 w-40 text-[11px] border border-slate-300 rounded px-1.5" data-testid={`support-${field}-input-${t.id}`} />
        <button type="button" onClick={save} className="p-0.5 rounded bg-slate-900 text-white" aria-label="Save" data-testid={`support-${field}-save-${t.id}`}><Check className="w-3 h-3" /></button>
        <button type="button" onClick={() => setEditing(false)} className="p-0.5 rounded border text-slate-500" aria-label="Cancel"><X className="w-3 h-3" /></button>
      </span>
    );
  }
  if (!current) {
    return <button type="button" onClick={(e) => { e.stopPropagation(); setVal(''); setEditing(true); }} className="inline-flex items-center gap-0.5 text-[11px] text-slate-400 hover:text-slate-700" data-testid={`support-${field}-add-${t.id}`}><Plus className="w-3 h-3" /> {placeholder}</button>;
  }
  return (
    <span className="group/c inline-flex items-center gap-1 min-w-0">
      <a href={`${href}${current}`} onClick={(e) => e.stopPropagation()} className="text-[11px] text-slate-700 hover:text-slate-900 inline-flex items-center gap-1 truncate" data-testid={`support-${field === 'installer_phone' ? 'phone' : 'email'}-${t.id}`}><Icon className="w-3 h-3 shrink-0" /> <span className="truncate">{current}</span></a>
      <button type="button" onClick={(e) => { e.stopPropagation(); setVal(current); setEditing(true); }} className="opacity-0 group-hover/c:opacity-100 focus:opacity-100 text-slate-400 hover:text-slate-700" aria-label={`Edit ${placeholder}`} data-testid={`support-${field}-edit-${t.id}`}><Pencil className="w-3 h-3" /></button>
    </span>
  );
}

export function ContactDetails({ ticket, onPatch }) {
  return (
    <div className="flex flex-col gap-0.5">
      <ContactField ticket={ticket} field="installer_phone" icon={Phone} href="tel:" placeholder="Add phone" type="tel" onPatch={onPatch} />
      <ContactField ticket={ticket} field="installer_email" icon={Mail} href="mailto:" placeholder="Add email" type="email" onPatch={onPatch} />
    </div>
  );
}
